import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, ChevronRight, HelpCircle, Mail } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Button } from '../components/ui/button';

const FAQ_DATA = [
  {
    category: 'Commande & Paiement',
    color: '#E8945A',
    questions: [
      {
        q: 'Comment passer commande ?',
        a: "1. Vous choisissez vos articles dans la page Nos Produits. 2. Vous indiquez votre adresse de livraison (le logement) et, si elle est différente, votre adresse de facturation. 3. Vous renseignez vos dates d'arrivée et de départ. 4. Vous acceptez les CGV. 5. Vous êtes redirigé vers le paiement sécurisé Mollie pour régler par carte bancaire ou Apple Pay. La commande n'est confirmée qu'après réception du paiement.",
      },
      {
        q: 'Quels moyens de paiement acceptez-vous ?',
        a: "Le paiement se fait exclusivement en ligne, en une seule fois, via la plateforme sécurisée Mollie. Sont acceptés : Carte Bancaire (CB), Visa, Mastercard, Apple Pay et Bancontact. Aucun paiement par chèque, espèces ou virement n'est accepté.",
      },
      {
        q: "Combien de temps à l'avance dois-je commander ?",
        a: "Pour bénéficier de la livraison standard gratuite, votre commande doit être passée au moins 3 jours pleins avant votre arrivée, avec une coupure quotidienne à 16h00 (heure française). Exemple : commande passée lundi avant 16h → livraison gratuite à partir du jeudi ; commande passée lundi après 16h → livraison gratuite à partir du vendredi. Les commandes plus tardives sont possibles mais entraînent un supplément forfaitaire de 40 € TTC pour livraison express.",
      },
      {
        q: "Y a-t-il des frais en plus du prix des articles ?",
        a: "Toute commande inclut des frais de dossier de 1,00 € TTC (forfait administratif). En dehors de ce forfait et de la livraison express éventuelle (40 € si commande passée moins de 3 jours avant l'arrivée, avec coupure à 16h), il n'y a aucun autre frais : pas d'abonnement, pas d'engagement.",
      },
      {
        q: 'Mon paiement a échoué, que faire ?',
        a: "Si votre paiement est refusé, expiré ou annulé, aucune somme n'est débitée et la commande n'est pas créée. Vous pouvez recommencer en repartant de votre panier, ou essayer un autre moyen de paiement (Apple Pay, autre carte). En cas de souci persistant, contactez-nous à contact@terreetmer-locationdelinge.fr.",
      },
      {
        q: "Recevrai-je une facture ?",
        a: "Oui. Une facture conforme à la loi française (numérotée 2026-XXX, avec mentions légales obligatoires et CGV annexées) est automatiquement générée dès la confirmation du paiement. Elle est téléchargeable à tout moment depuis votre espace personnel (Mon espace → bouton PDF).",
      },
    ],
  },
  {
    category: 'Annulation & Remboursement',
    color: '#DC2626',
    questions: [
      {
        q: 'Puis-je annuler ma commande après le paiement ?',
        a: "Non. Toute commande payée est ferme et définitive : aucun remboursement, total ou partiel, n'est possible, quelle qu'en soit la raison (annulation de séjour, modification de date, non-utilisation, restitution anticipée). Conformément à l'article L.221-28 du Code de la consommation et à l'Article 22 de nos CGV, en validant votre commande vous demandez l'exécution immédiate du service et renoncez expressément à votre droit de rétractation. Cette politique est acceptée via la case à cocher au moment du paiement.",
      },
      {
        q: "Et si j'annule mon séjour, change de date ou n'utilise finalement pas le linge ?",
        a: "Aucun remboursement n'est possible, qu'il soit total ou partiel, dans tous ces cas — sans exception. Le service ayant été engagé dès la validation de votre commande, la prestation est due. Cette politique est précisée dans nos CGV (Article 22) et acceptée via la case à cocher au moment du paiement.",
      },
      {
        q: "Et si je me trompe de date ou d'adresse ?",
        a: "Contactez-nous immédiatement (avant la livraison) à contact@terreetmer-locationdelinge.fr en précisant votre numéro de commande. Si la livraison n'a pas encore été préparée, nous ferons notre maximum pour ajuster. Une fois la livraison enclenchée, aucune modification n'est possible.",
      },
      {
        q: "Y a-t-il une caution prélevée ?",
        a: "Lors du paiement, une empreinte de votre carte bancaire est conservée à titre de caution, plafonnée à 150 € maximum, pour couvrir d'éventuels vols ou dégradations du linge de lit et des serviettes. En l'absence de litige à la restitution, l'empreinte est automatiquement libérée et aucun montant n'est prélevé. Détails à l'Article 16 des CGV.",
      },
    ],
  },
  {
    category: 'Livraison & Récupération',
    color: '#5493CC',
    questions: [
      {
        q: 'Quels sont les délais et horaires de livraison ?',
        a: "Nous livrons votre linge directement dans votre logement le jour de votre arrivée, généralement entre 10h et 16h (sauf cas particulier précisé lors de la commande). Pour une arrivée anticipée, indiquez-le simplement dans les instructions spéciales lors du paiement.",
      },
      {
        q: 'Livrez-vous toute la Bretagne ?',
        a: "Nous opérons dans nos zones d'implantation : la Côte d'Émeraude (35), une partie des Côtes-d'Armor (22) et le Sud-Morbihan autour de Damgan / La Roche-Bernard (56). Consultez la page Nos Implantations pour la liste complète des communes desservies.",
      },
      {
        q: "Que faire si je ne suis pas sur place à l'arrivée ?",
        a: "Indiquez-nous lors du paiement le mode d'accès au logement (boîte à clés, code, contact concierge). Nous déposons le linge à l'intérieur du logement de manière sécurisée. Aucune signature n'est requise.",
      },
      {
        q: 'Comment se passe la récupération du linge sale en fin de séjour ?',
        a: "À la fin de votre séjour, laissez simplement le linge utilisé dans le sac à linge fourni, sur place dans le logement. Nos équipes ou la conciergerie partenaire récupèrent l'ensemble après votre départ. Aucune action de votre part n'est nécessaire — vous partez tranquille.",
      },
      {
        q: "Que faire si le colis est endommagé à la livraison ?",
        a: "Si l'emballage extérieur est ouvert ou abîmé, contactez-nous immédiatement à contact@terreetmer-locationdelinge.fr (Article 11 des CGV). Toute réclamation pour erreur de livraison ou non-conformité doit être faite le jour même de la livraison.",
      },
    ],
  },
  {
    category: 'Produits & Qualité',
    color: '#5493CC',
    questions: [
      {
        q: 'Quels articles puis-je louer ?',
        a: "Deux familles : les Kits Draps (drap plat + housse de couette + taies, en tailles S / M / L / XL selon votre lit) et le Linge de toilette (serviettes 70 × 140 cm, tapis de bain 50 × 70 cm, torchons 40 × 40 cm). Voir la page Nos Produits pour les prix actuels.",
      },
      {
        q: 'Quel Kit Draps choisir pour mon lit ?',
        a: "Kit S = lit 1 place 90 cm. Kit M = lit 2 places 140 cm. Kit L = lit 2 places 160 cm. Kit XL = lit 2 places 180 cm (King Size). En cas de doute, mesurez la largeur de votre matelas. Vous pouvez aussi nous contacter avant commande, on vous oriente.",
      },
      {
        q: 'Quelle est la qualité du linge proposé ?',
        a: "Notre linge est confectionné en 100 % polyester recyclé ou en coton 100 % selon les références. Coloris des draps : gris ou vert, certaines pièces avec motifs. Coloris du linge de toilette (serviettes, tapis de bain, torchons) : gris, beige, bleu ou vert. L'ensemble est lavé et repassé dans un atelier professionnel selon des normes d'hygiène strictes (lavage haute température, contrôle qualité avant chaque livraison).",
      },
      {
        q: "Le linge est-il éco-responsable ?",
        a: "Oui. Notre linge est traité dans des blanchisseries professionnelles qui mutualisent les lavages, ce qui consomme jusqu'à 60 % d'eau et d'énergie en moins qu'un lavage domestique. C'est l'option la plus écologique pour vos vacances : moins de transport (pas de linge dans la valise), moins de lessive à la maison.",
      },
      {
        q: "Que se passe-t-il en cas de tache, déchirure ou perte ?",
        a: "Le linge est destiné à un usage normal : taches d'usage léger acceptées, sans surcoût. En cas de perte, vol ou dégradation importante (déchirures, brûlures, taches indélébiles), le tarif de remplacement par article s'applique (voir grille Article 13 des CGV) et est prélevé sur l'empreinte de caution dans la limite de 150 €.",
      },
    ],
  },
  {
    category: 'Particuliers, Propriétaires & Pros',
    color: '#E8945A',
    questions: [
      {
        q: "Je suis propriétaire d'une location saisonnière, que proposez-vous ?",
        a: "Nous accompagnons les propriétaires de gîtes, meublés de tourisme et locations courte durée avec une solution clé en main : votre voyageur commande directement, ou vous gérez vous-même les commandes pour vos hôtes. Aucun engagement, aucun stock à gérer. Idéal pour proposer un service linge sans investissement initial.",
      },
      {
        q: 'Êtes-vous un partenaire pour les conciergeries et professionnels ?',
        a: "Oui. Nous travaillons avec les agences immobilières, conciergeries Airbnb, campings et résidences de tourisme. Solutions tarifaires adaptées au volume, facturation centralisée, planning de livraison récurrent. Contactez-nous à contact@terreetmer-locationdelinge.fr pour une étude personnalisée.",
      },
      {
        q: "Quel est le lien avec Terre et Mer Conciergerie ?",
        a: "TERRE ET MER LOCATION DE LINGE est exploité par MCB (SAS) en lien avec la conciergerie Terre et Mer (terreetmer-conciergerie.fr) qui s'occupe de la gestion complète de locations saisonnières sur la Côte d'Émeraude. Les deux services sont indépendants mais complémentaires.",
      },
    ],
  },
  {
    category: 'Espace personnel & Contact',
    color: '#5493CC',
    questions: [
      {
        q: "Comment accéder à mon espace personnel ?",
        a: "Cliquez sur l'icône utilisateur en haut à droite, puis Connexion (ou Créer un compte si c'est votre première commande). Dans Mon espace, vous retrouvez l'historique de toutes vos commandes ainsi que vos factures PDF prêtes à télécharger.",
      },
      {
        q: 'Comment télécharger ma facture ?',
        a: "Connectez-vous à Mon espace, retrouvez votre commande dans la liste, et cliquez sur le bouton orange « PDF » à droite. Le téléchargement de la facture n'est possible qu'une fois le paiement confirmé par Mollie (statut « Confirmée »).",
      },
      {
        q: 'Comment vous contacter ?',
        a: "Par email à contact@terreetmer-locationdelinge.fr — du lundi au vendredi de 10h à 12h et de 14h à 17h, samedi de 10h à 12h. Toutes les demandes reçoivent une réponse sous 24h ouvrées maximum.",
      },
    ],
  },
];

const FAQPage = () => {
  const [search, setSearch] = useState('');

  const filteredData = FAQ_DATA.map((cat) => ({
    ...cat,
    questions: cat.questions.filter(
      (item) =>
        item.q.toLowerCase().includes(search.toLowerCase()) ||
        item.a.toLowerCase().includes(search.toLowerCase())
    ),
  })).filter((cat) => cat.questions.length > 0);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#2D3E50] to-[#3D5266] text-white py-20 md:py-28 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#E8945A] font-medium mb-4">
            <HelpCircle className="h-4 w-4" />
            Centre d'aide
          </span>
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl tracking-tight font-medium">
            Questions fréquentes
          </h1>
          <p className="text-white/70 mt-6 max-w-2xl mx-auto text-lg">
            Tout ce que vous devez savoir avant de commander votre linge.
            Une question particulière ? Cherchez ci-dessous ou contactez-nous.
          </p>

          {/* Search */}
          <div className="mt-10 max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-white/40" />
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher une question..."
              className="pl-12 h-14 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:bg-white/15 rounded-xl"
              data-testid="faq-search"
            />
          </div>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-16">
        {filteredData.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-[#64748B] text-lg">
              Aucune question ne correspond à votre recherche.
            </p>
            <Button
              variant="outline"
              className="mt-6"
              onClick={() => setSearch('')}
            >
              Réinitialiser
            </Button>
          </div>
        ) : (
          filteredData.map((cat) => (
            <div key={cat.category} className="mb-12" data-testid={`faq-cat-${cat.category}`}>
              <div className="flex items-center gap-3 mb-6">
                <span
                  className="w-1 h-7 rounded-full"
                  style={{ backgroundColor: cat.color }}
                />
                <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-medium text-[#2D3E50]">
                  {cat.category}
                </h2>
                <span className="text-sm text-[#64748B] ml-2">
                  {cat.questions.length} question{cat.questions.length > 1 ? 's' : ''}
                </span>
              </div>

              <Accordion type="single" collapsible className="space-y-3">
                {cat.questions.map((item, i) => (
                  <AccordionItem
                    key={i}
                    value={`${cat.category}-${i}`}
                    className="bg-[#FAFAFA] hover:bg-[#F5F5F5] transition-colors rounded-xl border border-gray-100 px-6"
                  >
                    <AccordionTrigger className="text-left font-['Playfair_Display'] font-medium text-[#2D3E50] hover:text-[#E8945A] py-5 text-base">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-[#475569] pb-5 leading-relaxed">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))
        )}

        {/* Contact CTA */}
        <div className="mt-20 rounded-2xl bg-gradient-to-br from-[#FFF5ED] to-[#FFFBF5] border border-[#E8945A]/20 p-8 md:p-12 text-center">
          <Mail className="h-10 w-10 text-[#E8945A] mx-auto mb-4" />
          <h3 className="font-['Playfair_Display'] text-2xl md:text-3xl font-medium text-[#2D3E50] mb-3">
            Vous n'avez pas trouvé votre réponse ?
          </h3>
          <p className="text-[#64748B] mb-6 max-w-xl mx-auto">
            Notre équipe répond à toutes vos questions par email du lundi au vendredi (10h–12h · 14h–17h) et le samedi (10h–12h).
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              asChild
              className="bg-[#E8945A] hover:bg-[#D4844A] rounded-lg h-12 px-6"
              data-testid="faq-contact-btn"
            >
              <a href="mailto:contact@terreetmer-locationdelinge.fr">
                contact@terreetmer-locationdelinge.fr
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              className="rounded-lg h-12 px-6 border-[#2D3E50] text-[#2D3E50]"
            >
              <Link to="/products">
                Voir nos produits
                <ChevronRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
